// Parsing and matching for bulk device selection by serial number
// (pasted text or a CSV file). Matching is case-insensitive and ignores
// surrounding spaces and quotes.

export interface ISerialDevice {
  serial?: string | null;
}

export interface ISerialMatchResult<T> {
  /** Devices found, in the order their serials were given. */
  matched: T[];
  /** Serials not found among the available or already assigned devices. */
  notFound: string[];
  /** Serials of devices that are already assigned (nothing to add). */
  alreadyAssigned: string[];
  /** Serials given more than once (repeats are ignored). */
  duplicates: number;
}

export interface ISerialCsvResult {
  serials: string[];
  error?: string;
}

export const SERIAL_CSV_HEADER = 'serial';
export const EXAMPLE_SERIALS_CSV = `${SERIAL_CSV_HEADER}\n0DREHCXW601535J\n0H9UHNMT800039J\n0JB8HNQT700003F\n`;

const SERIAL_HEADERS = ['serial', 'serials', 'serial number', 'serial_number', 'serialnumber', 'serial no', 'sn'];

/** Splits pasted text on commas, semicolons, tabs, spaces and new lines. */
export function parseSerials(text: string): string[] {
  return (text || '')
    .split(/[\s,;]+/)
    .map(cleanCell)
    .filter((serial) => !!serial);
}

/**
 * Reads serials from a CSV file: the "serial" column when there is a header
 * (comma or semicolon separated), or every value of a one-column file.
 */
export function parseSerialsCsv(content: string): ISerialCsvResult {
  const lines = (content || '')
    .replace(/^﻿/, '')
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);
  if (!lines.length) {
    return { serials: [], error: 'The file is empty.' };
  }

  const delimiter = lines[0].includes(';') && !lines[0].includes(',') ? ';' : ',';
  const rows = lines.map((line) => line.split(delimiter).map(cleanCell));
  const header = rows[0].map((cell) => cell.toLowerCase());
  const serialColumn = header.findIndex((cell) => SERIAL_HEADERS.includes(cell));

  if (serialColumn >= 0) {
    return { serials: rows.slice(1).map((row) => row[serialColumn] || '').filter((serial) => !!serial) };
  }
  if (rows.every((row) => row.length === 1)) {
    return { serials: rows.map((row) => row[0]).filter((serial) => !!serial) };
  }
  return { serials: [], error: `No "${SERIAL_CSV_HEADER}" column found. Use the example file as a template.` };
}

export function matchSerials<T extends ISerialDevice>(
  serials: string[],
  available: T[],
  assigned: ISerialDevice[] = []
): ISerialMatchResult<T> {
  const availableBySerial = new Map<string, T>();
  for (const device of available) {
    const key = serialKey(device.serial);
    if (key && !availableBySerial.has(key)) availableBySerial.set(key, device);
  }
  const assignedSerials = new Set(assigned.map((device) => serialKey(device.serial)).filter((key) => !!key));

  const result: ISerialMatchResult<T> = { matched: [], notFound: [], alreadyAssigned: [], duplicates: 0 };
  const seen = new Set<string>();
  for (const serial of serials) {
    const key = serialKey(serial);
    if (!key) continue;
    if (seen.has(key)) {
      result.duplicates++;
      continue;
    }
    seen.add(key);

    const device = availableBySerial.get(key);
    if (device) result.matched.push(device);
    else if (assignedSerials.has(key)) result.alreadyAssigned.push(serial);
    else result.notFound.push(serial);
  }
  return result;
}

/** One-column CSV with a header row; values with commas or quotes are quoted. */
export function toCsv(header: string, values: string[]): string {
  const escape = (value: string) => /[",;\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  return [header, ...values].map(escape).join('\n') + '\n';
}

function serialKey(serial: string | null | undefined): string {
  return cleanCell(serial || '').toUpperCase();
}

function cleanCell(value: string): string {
  return value.trim().replace(/^"(.*)"$/, '$1').trim();
}
